import { useTranslation } from "next-i18next";
import { IFiguresPickerData } from "src/api/server/extended-search.types";
import { SearchGridResults } from "src/components/pages/search/components/grid/search-grid-results";
import { SearchTableResults } from "src/components/pages/search/components/table/search-table-results";
import { useSearchGridResults } from "src/components/pages/search/hooks/use-search-grid-results";
import { useSearchTableResults } from "src/components/pages/search/hooks/use-search-table-results";
import { useTableFigures } from "src/components/pages/search/hooks/use-table-figures";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { ViewMode, useSearchStore } from "src/components/pages/search/store/search-store";
import { EmptyState } from "src/components-mui/common/empty-state/empty-state";
import { getGlobals } from "src/constants/globals";
import { useIsMounted } from "src/hooks/use-is-mounted";
import { useMinimumDisplayTime } from "./hooks/use-minimum-display-time";
import { SEARCH_RESULT_SECTION_NO_RESULTS_TEST_ID } from "./result-section.test-ids";

interface IResultSectionProps {
    searchParams: ISearchRequestParamsOmitCountry | null;
    figuresPicker?: IFiguresPickerData;
}

const MIN_LOADER_DISPLAY_TIME = 400;

export const ResultSection = ({ searchParams, figuresPicker }: IResultSectionProps) => {
    const { view } = useSearchStore();
    const isTableView = view === ViewMode.Table;
    const { results: cards, isFailed, isLoading, isFetchingNextPage, hasNextPage, onLoadMore } = useSearchGridResults(searchParams, !isTableView);
    const tableFigures = useTableFigures(figuresPicker, searchParams);
    const tableResults = useSearchTableResults({ searchParams, enabled: isTableView });
    const currency = getGlobals()?.currency;
    const { t } = useTranslation("extended-search");
    const isMounted = useIsMounted();
    const shouldShowLoader = isMounted && isLoading;
    const showLoadingSkeleton = useMinimumDisplayTime(shouldShowLoader, MIN_LOADER_DISPLAY_TIME) || !cards;

    const isError = isTableView ? tableResults.isError : isFailed;
    const isEmpty = isTableView ? tableResults.hasNoData : cards?.length === 0 && !showLoadingSkeleton;

    if (isError) {
        return <EmptyState title={t("search-results.search-error-title")} description={t("search-results.search-error-description")} />;
    }

    if (isEmpty) {
        return (
            <EmptyState
                title={t("search-results.no-search-results-title")}
                description={t("search-results.no-search-results-description")}
                data-test-id={SEARCH_RESULT_SECTION_NO_RESULTS_TEST_ID}
            />
        );
    }

    if (isTableView) {
        return (
            <SearchTableResults
                data={tableResults.data}
                figures={tableFigures}
                rowCount={tableResults.rowCount}
                isLoading={tableResults.isPending}
                sorting={tableResults.sorting}
                onSortingChange={tableResults.onSortingChange}
                pagination={tableResults.pagination}
                onPaginationChange={tableResults.onPaginationChange}
            />
        );
    }

    return (
        <SearchGridResults
            cards={cards}
            currency={currency}
            showLoadingSkeleton={showLoadingSkeleton}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
        />
    );
};
